import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Megaphone, Plus, Search } from "lucide-react";
import { CAMPAIGN_LIST_PAGE_SIZES } from "@multi-chat/shared";
import { ActiveFilterBar, activeFilterChip } from "../../components/common/ActiveFilterBar/ActiveFilterBar.js";
import { WorkspaceDrawerTrigger } from "../../components/layout/WorkspaceDrawerTrigger.js";
import { Button } from "../../components/ui/button/index.js";
import { EmptyState } from "../../components/common/EmptyState/index.js";
import { cn } from "../../lib/utils.js";
import { contactsTableShellClass } from "../contacts/contacts-table-presentation.js";
import { CampaignsBulkActionsBar } from "./CampaignsBulkActionsBar.js";
import { CampaignsListToolbarTools } from "./CampaignsListToolbarTools.js";
import { CampaignsTable } from "./CampaignsTable.js";
import { useCampaignWorkspace } from "./CampaignWorkspaceLayout.js";
import {
  CAMPAIGN_LIST_SEARCH_DEBOUNCE_MS,
  CAMPAIGN_LIST_SEARCH_MAX_LENGTH,
  CAMPAIGN_LIST_NAV_ITEMS,
  campaignListHref,
  canMutateCampaignAudience,
} from "./campaigns-presentation.js";
import { mapCampaignError } from "./campaigns-errors.js";
import { useNotifications } from "../notifications/notification-context.js";
import { useCampaignTableLayout } from "./use-campaign-table-layout.js";

export function CampaignsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { pushNotification } = useNotifications();
  const navigate = useNavigate();
  const { list, canCreate, role, selectedCampaignId, openMobileNav, mobileNavOpen } =
    useCampaignWorkspace();
  const { search: committedSearch, setSearch } = list;
  const [searchInput, setSearchInput] = useState(list.search);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [copiedNotice, setCopiedNotice] = useState(false);
  const canDelete = canMutateCampaignAudience(role);
  const table = useCampaignTableLayout(canDelete);
  const searchActive = list.search.trim().length > 0;
  const statusFilter = list.status;
  const rangeStart = list.totalCount === 0 ? 0 : (list.page - 1) * list.pageSize + 1;
  const rangeEnd = Math.min(list.page * list.pageSize, list.totalCount);
  const allHref = campaignListHref({
    search: list.search,
    page: 1,
    pageSize: list.pageSize,
    sort: list.sort,
  });

  useEffect(() => {
    setSearchInput(committedSearch);
  }, [committedSearch]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchInput.trim() === committedSearch) return;
      setSearch(searchInput);
    }, CAMPAIGN_LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [committedSearch, searchInput, setSearch]);

  useEffect(() => {
    if (!copiedNotice) return;
    const handle = window.setTimeout(() => setCopiedNotice(false), 1800);
    return () => window.clearTimeout(handle);
  }, [copiedNotice]);

  const emptyBody = (() => {
    if (searchActive) {
      return (
        <div
          className="grid justify-items-center gap-2 px-4 py-12 text-center"
          data-testid="campaign-list-search-empty"
        >
          <p className="m-0 text-[0.9375rem] font-semibold">{t("campaigns.list.noResults")}</p>
          <p className="m-0 text-[0.8125rem] text-muted">{t("campaigns.list.noResultsHelp")}</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setSearch("")}
              data-testid="campaign-list-clear-search-empty"
            >
              {t("campaigns.list.clearSearch")}
            </Button>
            {statusFilter != null ? (
              <Button size="sm" variant="ghost" asChild>
                <Link to={allHref}>{t("campaigns.nav.all")}</Link>
              </Button>
            ) : null}
          </div>
        </div>
      );
    }

    if (statusFilter === "DRAFT") {
      return (
        <div
          className="grid justify-items-center gap-2 px-4 py-12 text-center"
          data-testid="campaign-list-empty-drafts"
        >
          <p className="m-0 text-[0.9375rem] font-semibold">{t("campaigns.list.emptyDrafts")}</p>
          {canCreate ? (
            <Button size="sm" variant="outline" asChild>
              <Link to="/campaigns/new">
                <Plus size={16} aria-hidden="true" />
                {t("campaigns.actions.new")}
              </Link>
            </Button>
          ) : null}
        </div>
      );
    }

    if (statusFilter === "SCHEDULED") {
      return (
        <div
          className="grid justify-items-center gap-2 px-4 py-12 text-center"
          data-testid="campaign-list-empty-scheduled"
        >
          <p className="m-0 text-[0.9375rem] font-semibold">{t("campaigns.list.emptyScheduled")}</p>
          <p className="m-0 max-w-md text-[0.8125rem] text-muted">
            {t("campaigns.list.emptyScheduledHelp")}
          </p>
        </div>
      );
    }

    if (statusFilter === "RUNNING") {
      return (
        <div
          className="grid justify-items-center gap-2 px-4 py-12 text-center"
          data-testid="campaign-list-empty-running"
        >
          <p className="m-0 text-[0.9375rem] font-semibold">{t("campaigns.list.emptyRunning")}</p>
        </div>
      );
    }

    if (statusFilter === "COMPLETED") {
      return (
        <div
          className="grid justify-items-center gap-2 px-4 py-12 text-center"
          data-testid="campaign-list-empty-completed"
        >
          <p className="m-0 text-[0.9375rem] font-semibold">{t("campaigns.list.emptyCompleted")}</p>
          <p className="m-0 max-w-md text-[0.8125rem] text-muted">
            {t("campaigns.list.emptyCompletedHelp")}
          </p>
        </div>
      );
    }

    return (
      <div className="grid justify-items-center gap-3 px-4 py-16 text-center" data-testid="campaign-list-empty">
        <span className="inline-grid size-10 place-items-center rounded-full border border-line/70 bg-surface text-accent">
          <Megaphone size={18} aria-hidden="true" />
        </span>
        <div className="grid max-w-md gap-1">
          <h2 className="m-0 text-[0.9375rem] font-semibold">{t("campaigns.list.emptyTitle")}</h2>
          <p className="m-0 text-[0.8125rem] text-muted">{t("campaigns.list.emptyDescription")}</p>
        </div>
        {canCreate ? (
          <Button size="sm" asChild>
            <Link to="/campaigns/new">
              <Plus size={16} aria-hidden="true" />
              {t("campaigns.actions.new")}
            </Link>
          </Button>
        ) : null}
      </div>
    );
  })();

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="campaigns-page">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(248,250,252,0.94)_100%)] px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <span className="inline-grid size-9 place-items-center rounded-xl border border-line/70 bg-surface text-accent shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
            <Megaphone size={18} aria-hidden="true" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-[1rem] font-semibold tracking-[-0.01em] text-foreground">
                {t("campaigns.title")}
              </h1>
            </div>
            <p className="text-[0.75rem] text-muted">{t("campaigns.list.subtitle")}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <WorkspaceDrawerTrigger
            label={t("campaigns.title")}
            open={mobileNavOpen}
            onClick={openMobileNav}
            testId="campaign-mobile-nav-trigger"
            ariaLabel={t("campaigns.nav.open")}
          />
          {canCreate ? (
            <Button size="sm" asChild>
              <Link to="/campaigns/new">
                <Plus size={16} aria-hidden="true" />
                {t("campaigns.actions.new")}
              </Link>
            </Button>
          ) : null}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b border-line/50 bg-surface/80 px-4 py-2.5">
        <div className="relative min-w-0 flex-1 lg:max-w-md">
          <Search
            size={16}
            className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchInput}
            maxLength={CAMPAIGN_LIST_SEARCH_MAX_LENGTH}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={t("campaigns.list.searchPlaceholder")}
            aria-label={t("campaigns.list.search")}
            className="w-full rounded-lg border border-line/70 bg-background py-2 ps-9 pe-3 text-[0.8125rem] shadow-sm transition-[border-color,box-shadow] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color-mix(in_srgb,var(--color-accent)_55%,transparent)]"
            data-testid="campaign-list-search"
          />
        </div>
        <CampaignsListToolbarTools
          sort={list.sort}
          onSortChange={list.setSort}
          columnOrder={table.columnOrder}
          hiddenColumns={table.hiddenColumns}
          onToggleColumn={table.toggleColumn}
          onMoveColumn={table.moveColumn}
          onReorderColumn={table.reorderColumn}
          onResetColumns={table.resetColumns}
        />
      </div>
      <ActiveFilterBar
        chips={[
          activeFilterChip("search", t("campaigns.list.search"), list.search, () => {
            setSearchInput("");
            setSearch("");
          }),
          activeFilterChip(
            "status",
            t("campaigns.nav.filters"),
            list.status
              ? t(
                  CAMPAIGN_LIST_NAV_ITEMS.find((item) => item.status === list.status)?.labelKey ??
                    "campaigns.nav.filters",
                )
              : null,
            () => navigate(allHref),
          ),
        ].flatMap((chip) => (chip ? [chip] : []))}
        onClearAll={() => {
          setSearchInput("");
          setSearch("");
          navigate(
            campaignListHref({
              search: "",
              page: 1,
              pageSize: list.pageSize,
              sort: list.sort,
            }),
          );
        }}
        clearTestId="campaign-clear-all-filters"
      />

      {copiedNotice ? (
        <p
          className="mx-4 mt-2 rounded-md border border-line/70 bg-surface px-3 py-1.5 text-[0.75rem] text-muted"
          role="status"
          data-testid="campaign-id-copied"
        >
          {t("campaigns.list.idCopied")}
        </p>
      ) : null}

      {canDelete ? (
        <CampaignsBulkActionsBar
          selectedCount={list.selectedIds.size}
          busy={bulkBusy}
          onClearSelection={list.clearSelection}
          onDeleteSelected={async () => {
            const ids = [...list.selectedIds];
            setBulkBusy(true);
            setActionError(null);
            try {
              await list.deleteSelected(ids);
              pushNotification({ message: t("campaigns.toasts.deleted") });
              if (selectedCampaignId != null && ids.includes(selectedCampaignId)) {
                navigate(allHref, { replace: true });
              }
            } catch (caught) {
              setActionError(caught);
              pushNotification({
                kind: "error",
                message: mapCampaignError(t, caught, t("campaigns.errors.requestFailed")),
              });
            } finally {
              setBulkBusy(false);
            }
          }}
        />
      ) : null}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {actionError ? (
          <p className="m-4 shrink-0 rounded-md border border-error/40 bg-error-muted px-3 py-2 text-[0.8125rem] text-error" role="alert">
            {mapCampaignError(t, actionError, t("campaigns.errors.requestFailed"))}
          </p>
        ) : null}
        {list.loading ? (
          <div className="min-w-0 p-4" data-testid="campaign-list-loading">
            <div className={cn(contactsTableShellClass, "max-w-full overflow-x-auto")}>
              <div className="grid gap-2 px-3 py-3" aria-busy="true" aria-live="polite">
                <span className="sr-only">{t("campaigns.loading")}</span>
                <div className="h-7 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
                <div className="h-7 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
                <div className="h-7 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
                <div className="h-7 animate-pulse rounded-md bg-surface-muted motion-reduce:animate-none" />
              </div>
            </div>
          </div>
        ) : list.error ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <EmptyState title={t("campaigns.errors.loadFailed")}>
              <p className="m-0 text-muted">
                {mapCampaignError(t, list.error, t("campaigns.errors.loadFailed"))}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => void list.reload()}>
                {t("campaigns.actions.retry")}
              </Button>
            </EmptyState>
          </div>
        ) : list.totalCount === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">{emptyBody}</div>
        ) : (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4">
            <CampaignsTable
              items={list.items}
              selectedIds={list.selectedIds}
              canDelete={canDelete}
              busy={bulkBusy}
              sort={list.sort}
              visibleColumns={table.visibleColumns}
              widths={table.widths}
              onContainerWidth={table.setContainerWidth}
              onToggleSelect={list.toggleSelect}
              onSelectPage={list.selectIds}
              onReorderColumn={table.reorderColumn}
              onResizeColumn={table.resizeColumn}
              onCopyId={async (id) => {
                try {
                  await navigator.clipboard.writeText(id);
                  setCopiedNotice(true);
                } catch {
                  setCopiedNotice(false);
                }
              }}
              onDelete={async (ids) => {
                setBulkBusy(true);
                setActionError(null);
                try {
                  await list.deleteSelected(ids);
                  pushNotification({ message: t("campaigns.toasts.deleted") });
                  if (selectedCampaignId != null && ids.includes(selectedCampaignId)) {
                    navigate(allHref, { replace: true });
                  }
                } catch (caught) {
                  setActionError(caught);
                  pushNotification({
                    kind: "error",
                    message: mapCampaignError(t, caught, t("campaigns.errors.requestFailed")),
                  });
                } finally {
                  setBulkBusy(false);
                }
              }}
            />
          </div>
        )}
      </div>

      <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border/60 bg-surface px-4 py-2.5 text-[0.8125rem]">
        <span className="text-muted">
          {t("campaigns.pagination.viewing", {
            start: rangeStart,
            end: rangeEnd,
            total: list.totalCount,
          })}
        </span>
        <div className="flex items-center gap-2">
          <select
            className="rounded-md border border-border bg-background px-2 py-1"
            value={list.pageSize}
            onChange={(event) => list.setPageSize(Number(event.target.value))}
            aria-label={t("campaigns.pagination.pageSize")}
          >
            {CAMPAIGN_LIST_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={list.loading || list.page <= 1}
            onClick={() => list.setPage(list.page - 1)}
          >
            {t("campaigns.pagination.previous")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={list.loading || list.page >= Math.max(list.totalPages, 1)}
            onClick={() => list.setPage(list.page + 1)}
          >
            {t("campaigns.pagination.next")}
          </Button>
        </div>
      </footer>
      <Outlet />
    </div>
  );
}
